import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useUI } from 'src/sdk/ui'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { makeAddToCartEvent, sendEvent } from 'src/utils/restructure/analytics'
import { useCartFeedback } from 'src/components/contexts/CartFeedbackContext'
import { isPreSale } from 'src/utils/pdp/isPreSale'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { PAGE_TYPE_MAP } from 'src/hooks/usePageType'
import { MINICART_CROSS_SELL_EVENT_SOURCE } from 'src/components/cart/CartFeedback/constants'

import { useBuyButton } from '../../sections/Sidebar/ProductAction/BuyButton/hooks/useBuyButton'
import type { ProductItem } from '../../types/productItem'
import type { Product } from '../../types/product'
import { publish } from '../../productEvents'
import { getProductListName } from '../utils/getProductList'

type UseAddToCartLogicProps = {
  product: Product
  currentSku: ProductItem
  cartWasClearedRef: React.MutableRefObject<boolean>
  title?: string
  customFnOnAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
  eventSource?: string
  addLocation?: string
}

export type AddToCartFn = (
  type: 'addToCart' | 'buyNow',
  sku: ProductItem,
  e: React.MouseEvent<HTMLButtonElement>
) => Promise<void>

export const useAddToCartLogic = ({
  product,
  currentSku,
  cartWasClearedRef,
  customFnOnAdd,
  title,
  eventSource,
  addLocation,
}: UseAddToCartLogicProps) => {
  const { openCartFeedback } = useCartFeedback()
  const { pageData } = useGlobalContext()
  const {
    orderForm: { items },
    addItems,
  } = useCheckout()

  const { addToCart } = useBuyButton({
    propCurrentSku: currentSku,
  })

  const { openMinicart } = useUI()
  const { closeModal } = useModalSheet()

  const qttyAlreadyInCart = items?.find(
    (item) => item.id === currentSku.itemId
  )?.quantity

  const addToCartAfterClear = async (
    type: 'addToCart' | 'buyNow',
    sku: ProductItem
  ) => {
    const clearedCartItem = {
      id: sku.itemId,
      productId: product.productId,
      quantity: 1,
      seller: sku.sellerDefault.sellerId,
      index: 0,
      price: sku.offerDefault?.price ?? 0,
    }

    await addItems([clearedCartItem])

    const clearedCartEvent = makeAddToCartEvent([{ product, productItem: sku }])

    sendEvent(clearedCartEvent)
    closeModal?.()

    if (type === 'addToCart') {
      openMinicart()
    }
  }

  const addToCartNormal = async (
    type: 'addToCart' | 'buyNow',
    sku: ProductItem
  ) => {
    const itemToAdd = {
      id: sku.itemId,
      productId: product.productId,
      isPreSale: isPreSale(sku.estimatedDateArrival),
      quantity: qttyAlreadyInCart ? qttyAlreadyInCart + 1 : 1,
      seller: sku.sellerDefault.sellerId,
      index: 0,
      price: sku.offerDefault?.price ?? 0,
    }

    const isPLP =
      pageData.currentPageType === PAGE_TYPE_MAP.collection ||
      pageData.currentPageType === PAGE_TYPE_MAP.Pesquisa ||
      pageData.currentPageType === PAGE_TYPE_MAP.category

    const isMiniCartCrossSell = eventSource === MINICART_CROSS_SELL_EVENT_SOURCE

    const productListName = getProductListName({
      isMiniCartCrossSell,
      isPLP,
      currentPageType: pageData.currentPageType,
      title,
    })

    await addToCart({
      item: itemToAdd,
      type,
      eventData: {
        'modal nome': `adicionar ao carrinho - ${currentSku?.name}`,
        'product list type': pageData.currentPageType,
        'product list name': productListName,
        'add location': addLocation ?? pageData.currentPageType,
      },
      origin:
        type === 'buyNow'
          ? 'Botão comprar agora (modal)'
          : 'Botão adicionar ao carrinho (modal)',
      eventSource,
      shouldOpenFeedback: type !== 'buyNow',
    })

    const addToCartEvent = makeAddToCartEvent([{ product, productItem: sku }])

    closeModal?.()
    sendEvent(addToCartEvent)

    if (type !== 'buyNow') {
      openCartFeedback([itemToAdd.id])
    }
  }

  const addToCartFn = async (
    type: 'addToCart' | 'buyNow',
    sku: ProductItem,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    publish('addToCart')

    if (cartWasClearedRef.current) {
      cartWasClearedRef.current = false
      await addToCartAfterClear(type, sku)
    } else {
      await addToCartNormal(type, sku)
    }

    customFnOnAdd?.(e)
  }

  return { addToCartFn }
}
